import { Injectable } from '@angular/core';
import { filter, map, Observable, Subject, switchMap, take, throwError, withLatestFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '@environments/environment';
import { ResponseObjectSuccess, ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectAuthenticationResellerId, selectCredential } from '@store/selectors/authentication.selectors';
import { MenuItem } from 'primeng/api';
import { transformArrayUserData, transformDropdown, transformUserUpsert } from '@core/helpers';
import { Credential, Usuario, UsuarioForm } from '@core/models';
import { selectUserList, selectAuthenticationIsSuperUser, selectAuthenticationIsReseller } from '@store/selectors';
import { StateService } from '..';
import { StateEnum } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  credential$: Observable<Credential | null>;
  userList$: Observable<Usuario[] | null>;

  destroyTab$ = new Subject<void>();

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private findAllCompletedSource = new Subject<void>();
  findAllCompleted$ = this.findAllCompletedSource.asObservable();

  private closeFormSource = new Subject<void>();
  closeForm$ = this.closeFormSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>,
    private stateService: StateService
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.userList$ = this.store.select(selectUserList);
  }

  onDestroyTab(): void {
    this.destroyTab$.next();
    this.destroyTab$.complete();
    this.destroyTab$ = new Subject<void>();
  }

  removeCompleted(): void {
    this.closeForm();
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeForm();
  }
  
  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeForm();
  }

  findAllCompleted(): void {
    this.findAllCompletedSource.next();
  }

  closeForm(): void {
    this.closeFormSource.next();
  }

  find(id: string): Observable<ResponseSuccess<Usuario>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential.token && !credential?.granted)
          return throwError(() => new Error('No credential granted'));
        
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Usuario>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/usuario/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  findAll({ resellerId, search, stateId }: { resellerId?: string, search?: string, stateId?: string }): Observable<ResponseSuccess<Usuario>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      withLatestFrom(
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller)
      ),
      switchMap(([ credential, isSuperUser, isReseller ]) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!search && { nombre: search }),
          ...(!!stateId && { statusId: stateId }),
          ...(!!resellerId && { resellerId }),
          ...(!!isReseller && { resellerId: credential.idUsuario }),
          // TO DO - Preguntar si el soporte debe ver todos los usuarios que tiene el reseller
          ...(!isSuperUser && !isReseller && { resellerId: credential.resellerId }),
        };

        return this.http.get<ResponseSuccess<Usuario>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/usuario`,
          { 
            headers: { Authorization: token },
            params: { ...params },
          },
          
        );
      }),
      take(1),
    );
  }

  findAllWithState(): Observable<Usuario[]> {
    return this.userList$.pipe(
      withLatestFrom(this.stateService.getStateByName(StateEnum.Active)),
      map(([ userList, state ]) => ({ userList: userList as Usuario[], stateId: state?.idEstado })),
      map(({ userList, stateId }) => transformArrayUserData(userList, stateId)),
    )
  }

  filter(text: string): Observable<Usuario[]> {
    return this.userList$.pipe(
      filter((userList): userList is Usuario[] => userList !== null && userList.length > 0),
      map((userList) => userList.filter((user) => user.nombre.toLowerCase().includes(text.toLowerCase()))),
      take(1),
    );
  }

  create(userForm: UsuarioForm): Observable<ResponseObjectSuccess<Usuario>> {
    return this.credential$.pipe(
      withLatestFrom(
        this.store.select(selectAuthenticationResellerId),
      ),
      filter(([credential]) => !!credential?.granted),
      switchMap(([ credential, resellerId]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<Usuario>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/usuario`,
          transformUserUpsert({ ...userForm, resellerId: userForm.resellerId ?? resellerId, changePassword: true }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  update(id: string, userForm: UsuarioForm): Observable<ResponseObjectSuccess<Usuario>> {
    return this.credential$.pipe(
      withLatestFrom(
        this.store.select(selectAuthenticationResellerId),
      ),
      filter(([credential]) => !!credential?.granted),
      switchMap(([ credential, resellerId]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Usuario>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/usuario/${id}`,
          transformUserUpsert({ ...userForm, resellerId: userForm.resellerId ?? resellerId }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  activate(id: string): Observable<ResponseObjectSuccess<Usuario>> {
    return this.credential$.pipe(
      withLatestFrom(
        this.stateService.getStateByName(StateEnum.Active),
      ),
      filter(([credential]) => !!credential?.granted),
      switchMap(([ credential, state ]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Usuario>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/usuario/${id}`,
          transformUserUpsert({ stateId: state?.idEstado as string } as UsuarioForm),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  deactivate(id: string): Observable<ResponseObjectSuccess<Usuario>> {
    return this.credential$.pipe(
      withLatestFrom(
        this.stateService.getStateByName(StateEnum.Inactive),
      ),
      filter(([credential]) => !!credential?.granted),
      filter(([_, state]) => !!state),
      switchMap(([ credential, state]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Usuario>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/usuario/${id}/desactivar`,
          { estado_id: state?.idEstado },
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.userList$.pipe(
      filter((users): users is Usuario[] => !!users),
      map((users) => transformDropdown(users, 'id', 'fullname')),
    );
  }
}
